import classnames from 'classnames';
import { isNull } from 'lodash/fp';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Modal from 'react-modal';

import styles from './Modal.module.scss';
import { Position } from './PositionModal';
import { Icon } from '../icon/Icon';
import { Delete } from '../icons';

export interface DraggablePositionModalProps {
    closeModal: () => void;
    isOpen: boolean;
    initialPosition: Position | null;
    children?: React.ReactNode;
    testId: string;
    height?: string;
    width?: string;
    zIndex?: number;
    maxWidth?: string;
    padding?: string;
    renderLeftOfClick?: boolean;
    renderAboveClick?: boolean;
    shouldRepositionOnResize?: boolean;
}

export const DraggablePositionModal: React.FC<DraggablePositionModalProps> = ({
    closeModal,
    isOpen,
    initialPosition,
    children,
    testId,
    height = '80px',
    width = '200px',
    zIndex = 10,
    renderLeftOfClick = false,
    renderAboveClick = false,
    padding = '5px',
    maxWidth,
    shouldRepositionOnResize
}) => {
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [isHovering, setIsHovering] = useState<boolean>(false);
    const [position, setPosition] = useState<Position | null>(initialPosition);
    const [modalWidth, setModalWidth] = useState<number>(width.match(/(\d+)/) ? parseInt(width.match(/(\d+)/)![0]) : 0);
    const [modalHeight, setModalHeight] = useState<number>(height.match(/(\d+)/) ? parseInt(height.match(/(\d+)/)![0]) : 0);
    const positionModalRef = useRef<HTMLDivElement>(null);
    const startPos = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

    const showHover = useCallback(() => setIsHovering(true), [setIsHovering]);
    const hideHover = useCallback(() => setIsHovering(false), [setIsHovering]);

    const renderLeft = useMemo(() => {
        if (isNull(initialPosition)) {
            return false;
        }
        if (renderLeftOfClick) {
            return renderLeftOfClick;
        }

        return initialPosition.x + modalWidth > window.innerWidth;
    }, [renderLeftOfClick, initialPosition, modalWidth]);

    const leftPosition = useMemo(() => {
        if (!position) {
            return 0;
        }
        return renderLeft ? position.x - modalWidth : position.x;
    }, [renderLeft, position, modalWidth]);

    const getModalDimensions = useCallback(() => {

        if (shouldRepositionOnResize) {
            setPosition(initialPosition);
        }
        if (!isNull(positionModalRef) && !isNull(positionModalRef.current)) {
            setModalWidth(positionModalRef.current.clientWidth);
            setModalHeight(positionModalRef.current.clientHeight);
        }
    }, [positionModalRef, shouldRepositionOnResize, initialPosition]);

    const renderAbove = useMemo(() => {
        if (isNull(initialPosition)) {
            return false;
        }
        if (renderAboveClick) {
            return renderAboveClick;
        }
        return initialPosition.y + modalHeight > window.innerHeight;
    }, [renderAboveClick, initialPosition, modalHeight]);

    const topPosition = useMemo(() => {
        if (!position) {
            return 0;
        }
        return renderAbove ? position.y - modalHeight : position.y;
    }, [renderAbove, position, modalHeight]);

    useEffect(() => {
        const initialMountTimeout = setTimeout(() => getModalDimensions(), 50);
        window.addEventListener('resize', getModalDimensions);
        return () => {
            window.removeEventListener('resize', getModalDimensions);
            clearTimeout(initialMountTimeout);
        };
    }, [getModalDimensions]);

    const onMouseMove = useCallback((e: MouseEvent) => {
        if (isDragging) {
            setPosition({
                x: e.clientX - startPos.current.x,
                y: e.clientY - startPos.current.y,
            });
        }
    }, [isDragging]);

    const onMouseUp = useCallback(() => setIsDragging(false), []);

    const onMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        if (!isNull(position)) {
            setIsDragging(true);
            startPos.current = { x: e.clientX - position.x, y: e.clientY - position.y };
        }
    }, [position]);

    useEffect(() => {
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
    }, [onMouseMove, onMouseUp]);

    return (
        <Modal
            isOpen={isOpen}
            className={styles.draggablePositionModal}
            ariaHideApp={false}
            style={{
                overlay: {
                    display: 'flex',
                    zIndex,
                    position: 'absolute',
                    left: `${leftPosition - 1}px`,
                    top: `${topPosition - 1}px`,
                    width: `${modalWidth + 2}px`,
                    height: `${modalHeight + 2}px`,
                    borderRadius: '5px',
                    maxWidth
                }
            }}
            onRequestClose={closeModal}
        >
            <div className={styles.positionModalInputWrapper} style={{ height, width }} data-testid={`${testId}-position-modal-wrapper`} ref={positionModalRef}>
                <div
                    className={classnames(styles.draggableHandleWrapper, {
                        [styles.hoveringDraggableHandle]: isHovering && !isDragging,
                        [styles.draggingDraggableHandle]: isDragging
                    })}
                >
                    <div
                        className={styles.draggableHandle}
                        onMouseDown={onMouseDown}
                        onMouseEnter={showHover}
                        onMouseOut={hideHover}
                    />
                    <div className={styles.closeModalWrapper} onClick={closeModal}><Icon icon={Delete} fontSize={20} /></div>
                </div>
                <div className={styles.modalContentWrapper} style={{ padding, width: `calc(100% - ${padding} - ${padding})`, height: `calc(100% - ${padding} - ${padding} - 20px)` }}>
                    {children}
                </div>
            </div>
        </Modal>
    );
};
